import { useParams, Link } from "react-router-dom";
import Navbar from "@/components/feature/Navbar";
import Footer from "@/components/feature/Footer";
import ScrollToTop from "@/components/feature/ScrollToTop";
import Breadcrumb from "@/components/feature/Breadcrumb";
import SEOHelmet from "@/components/feature/SEOHelmet";
import JsonLd from "@/components/feature/JsonLd";
import ArticleCard from "@/components/feature/ArticleCard";
import { articles } from "@/mocks/articles";

export default function Article() {
  const { id } = useParams<{ id: string }>();
  const articleId = parseInt(id || "0", 10);

  const article = articles.find((a) => a.id === articleId);

  if (!article) {
    return (
      <div className="min-h-screen bg-background-50 flex items-center justify-center">
        <div className="text-center">
          <h1 className="text-2xl font-heading font-bold text-foreground-900 mb-4">
            文章未找到
          </h1>
          <Link
            to="/"
            className="text-primary-600 hover:text-primary-700 transition-colors text-sm"
          >
            返回首页
          </Link>
        </div>
      </div>
    );
  }

  const relatedArticles = articles
    .filter((a) => a.category === article.category && a.id !== article.id)
    .slice(0, 4);

  const canonicalUrl = `https://whxinantec.cn/article/${article.id}`;
  const articleUrl = canonicalUrl;

  const articleSchema = {
    "@context": "https://schema.org",
    "@type": "NewsArticle",
    headline: article.title,
    description: article.excerpt,
    image: article.imageUrl,
    datePublished: article.date,
    dateModified: article.date,
    author: {
      "@type": "Person",
      name: article.author,
    },
    publisher: {
      "@type": "Organization",
      name: "黑料bu打烊",
    },
    mainEntityOfPage: {
      "@type": "WebPage",
      "@id": articleUrl,
    },
  };

  return (
    <>
      <ScrollToTop />
      <SEOHelmet
        title={`${article.title} - 黑料bu打烊`}
        description={article.excerpt}
        keywords={article.tags.join(",") + ",黑料bu打烊,明星黑料,娱乐圈爆料"}
        canonicalUrl={canonicalUrl}
        ogType="article"
        ogImage={article.imageUrl}
        publishedTime={article.date}
        modifiedTime={article.date}
      />
      <JsonLd data={articleSchema} />

      <div className="min-h-screen bg-background-50">
        <Navbar />

        <main className="pt-16">
          <div className="bg-background-100">
            <div className="max-w-7xl mx-auto">
              <Breadcrumb
                items={[
                  { label: "首页", path: "/" },
                  {
                    label: article.categoryName,
                    path: `/category/${article.category}`,
                  },
                  { label: article.title },
                ]}
              />
            </div>
          </div>

          {/* Article Content */}
          <article className="w-full px-4 md:px-6 py-10 md:py-14">
            <div className="max-w-7xl mx-auto">
              <div className="flex flex-col lg:flex-row gap-8">
                {/* Main article */}
                <div className="flex-1 max-w-3xl">
                  {/* Featured Image */}
                  <div className="w-full aspect-[16/9] rounded-lg overflow-hidden mb-8">
                    <img
                      src={article.imageUrl}
                      alt={article.title}
                      className="w-full h-full object-cover"
                    />
                  </div>

                  {/* Category badge */}
                  <Link
                    to={`/category/${article.category}`}
                    className="inline-block px-3 py-1 text-xs font-medium rounded-full bg-accent-100 text-accent-700 hover:bg-accent-200 transition-colors mb-4 cursor-pointer"
                  >
                    {article.categoryName}
                  </Link>

                  {/* Title */}
                  <h1 className="text-2xl md:text-3xl font-heading font-bold text-foreground-900 mb-4 leading-tight">
                    {article.title}
                  </h1>

                  {/* Meta */}
                  <div className="flex flex-wrap items-center gap-4 text-sm text-foreground-500 mb-8 pb-6 border-b border-background-200">
                    <div className="flex items-center gap-1.5">
                      <i className="ri-user-line text-xs"></i>
                      <span>{article.author}</span>
                    </div>
                    <div className="flex items-center gap-1.5">
                      <i className="ri-calendar-line text-xs"></i>
                      <span>{article.date}</span>
                    </div>
                    <div className="flex items-center gap-1.5">
                      <i className="ri-eye-line text-xs"></i>
                      <span>{article.views.toLocaleString()} 阅读</span>
                    </div>
                  </div>

                  {/* Article body */}
                  <div
                    className="prose prose-lg max-w-none text-foreground-800 leading-relaxed"
                    dangerouslySetInnerHTML={{ __html: article.content }}
                  />

                  {/* Tags */}
                  <div className="flex flex-wrap items-center gap-2 mt-8 pt-6 border-t border-background-200">
                    <span className="text-sm text-foreground-500">
                      <i className="ri-price-tag-3-line mr-1"></i>
                      标签：
                    </span>
                    {article.tags.map((tag) => (
                      <span
                        key={tag}
                        className="px-3 py-1 text-xs rounded-full bg-secondary-100 text-secondary-700"
                      >
                        {tag}
                      </span>
                    ))}
                  </div>

                  {/* Share */}
                  <div className="flex items-center gap-3 mt-6">
                    <span className="text-sm text-foreground-500">分享：</span>
                    <button className="w-8 h-8 flex items-center justify-center rounded-full bg-background-100 hover:bg-primary-100 hover:text-primary-600 transition-colors text-foreground-600 cursor-pointer">
                      <i className="ri-weibo-line text-sm"></i>
                    </button>
                    <button className="w-8 h-8 flex items-center justify-center rounded-full bg-background-100 hover:bg-primary-100 hover:text-primary-600 transition-colors text-foreground-600 cursor-pointer">
                      <i className="ri-wechat-line text-sm"></i>
                    </button>
                    <button className="w-8 h-8 flex items-center justify-center rounded-full bg-background-100 hover:bg-primary-100 hover:text-primary-600 transition-colors text-foreground-600 cursor-pointer">
                      <i className="ri-link text-sm"></i>
                    </button>
                  </div>
                </div>

                {/* Sidebar */}
                <aside className="w-full lg:w-80 flex-shrink-0">
                  <div className="sticky top-20">
                    <div className="bg-background-100 rounded-lg p-5 mb-6">
                      <h3 className="text-base font-heading font-semibold text-foreground-900 mb-4 flex items-center gap-2">
                        <i className="ri-links-line text-accent-600"></i>
                        相关文章
                      </h3>
                      <div>
                        {relatedArticles.length > 0 ? (
                          relatedArticles.map((ra) => (
                            <ArticleCard
                              key={ra.id}
                              article={ra}
                              variant="compact"
                            />
                          ))
                        ) : (
                          <p className="text-sm text-foreground-500">
                            暂无相关文章
                          </p>
                        )}
                      </div>
                    </div>

                    <div className="bg-background-100 rounded-lg p-5">
                      <h3 className="text-base font-heading font-semibold text-foreground-900 mb-3 flex items-center gap-2">
                        <i className="ri-mail-send-line text-primary-600"></i>
                        爆料投稿
                      </h3>
                      <p className="text-sm text-foreground-600 mb-4 leading-relaxed">
                        你有独家黑料？欢迎向我们投稿！
                      </p>
                      <a
                        href="mailto:tips@whxinantec.cn"
                        className="inline-flex items-center gap-2 px-5 py-2.5 bg-primary-500 hover:bg-primary-600 text-background-50 font-medium rounded-full transition-all duration-300 whitespace-nowrap cursor-pointer text-sm w-full justify-center"
                      >
                        <i className="ri-send-plane-line"></i>
                        立即投稿
                      </a>
                    </div>
                  </div>
                </aside>
              </div>
            </div>
          </article>
        </main>

        <Footer />
      </div>
    </>
  );
}